import axios from 'axios';
import { queryClient } from './queryClient';

/**
 * Register default mutation functions for offline-capable mutations.
 *
 * When mutations are persisted to IndexedDB, only the state (data) is saved —
 * functions cannot be serialised. On app restart, TanStack Query needs to know
 * which function to call for each queued mutation key.
 *
 * Call this once at app startup, BEFORE hydrating persisted state.
 */
export function registerMutationDefaults(): void {
  // ── Expenses ──────────────────────────────────────────────
  queryClient.setMutationDefaults(['create-expense'], {
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await axios.post('/api/expenses', data);
      return res.data;
    },
  });

  queryClient.setMutationDefaults(['approve-expense'], {
    mutationFn: async ({ id, comment }: { id: string; comment?: string }) => {
      const res = await axios.post(`/api/expenses/${id}/approve`, { comment });
      return res.data;
    },
  });

  queryClient.setMutationDefaults(['reject-expense'], {
    mutationFn: async ({ id, comment }: { id: string; comment?: string }) => {
      const res = await axios.post(`/api/expenses/${id}/reject`, { comment });
      return res.data;
    },
  });

  queryClient.setMutationDefaults(['match-expense-receipt'], {
    mutationFn: async (data: {
      expenseId: string;
      receiptPoolId: string;
      job: string;
      workcentre?: string;
      activityCode?: string;
      forceMatch?: boolean;
    }) => {
      const res = await axios.post(`/api/expenses/${data.expenseId}/match`, data);
      return res.data;
    },
  });

  // ── Timesheets ────────────────────────────────────────────
  queryClient.setMutationDefaults(['save-timesheet'], {
    mutationFn: async ({ id, data }: { id: string; data: Record<string, unknown> }) => {
      const res = await axios.put(`/api/timesheets/${id}`, data);
      return res.data;
    },
  });

  queryClient.setMutationDefaults(['submit-timesheet'], {
    mutationFn: async ({ id }: { id: string }) => {
      const res = await axios.post(`/api/timesheets/${id}/submit`);
      return res.data;
    },
  });

  // ── Client Invoices ───────────────────────────────────────
  queryClient.setMutationDefaults(['approve-invoice'], {
    mutationFn: async ({ id, comment }: { id: string; comment?: string }) => {
      const res = await axios.post(`/api/client-invoices/${id}/approve`, { comment });
      return res.data;
    },
  });

  // More mutation defaults will be added as modules are migrated.
}
